import DispatchersMixin from '@/mixins/dispatchers';
import IntervenerStatusTimeline from '@/views/interveners-details/intervener-status-timeline';

export default {
    name: 'IntervenerInfoBox',

    props: ['intervener'],

    components: {
        IntervenerStatusTimeline
    },

    mixins: [DispatchersMixin],

    data() {
        return {
            isLoading: false,
            isOtpLoading: false,
            recoveryLink: null,
            generatedOtp: null,
            otpDeliveryChannel: null,
            otpRemainingSeconds: 0,
            otpTimer: null
        };
    },

    methods: {
        /**
         * @override
         */
        remove(intervener) {
            this.$intervenersService.deleteByUUIId(intervener.uuid).then(res => {
                this.closeActiveTab('interveners');
                this.$toast.success(`${res.result.http_code}`, `${res.result.info}`);
            });
        },

        async generateRecoveryLink() {
            this.isLoading = true;
            this.recoveryLink = null;
            try {
                 // Validar si tiene usuario antes de llamar al endpoint
                if (!this.intervener.uuid_user) {
                    this.$bvToast.toast('Este interviniente no tiene un usuario asociado.', {
                      title: 'Sin usuario',
                      variant: 'warning',
                      solid: true
                    });
                    return;
                  }
            
                const response = await this.$intervenersService.generateRecoveryLink(this.intervener.uuid);
            
                if (response.data.recovery_url) {
                    this.recoveryLink = response.data.recovery_url;
                } else {
                    this.$bvToast.toast('No se pudo generar el enlace de recuperación.', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                    });
                }
            } catch (error) {
                this.$bvToast.toast('Se produjo un error al generar el enlace de recuperación.', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                  });
                console.error(error);
            } finally {
              this.isLoading = false;
            }
        },          
        
        copyToClipboard() {
            navigator.clipboard.writeText(this.recoveryLink).then(() => {
                this.$bvToast.toast('Enlace copiado al portapapeles', {
                    title: 'Éxito',
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 2000,
                });
            });
        },

        copyOtpToClipboard() {
            navigator.clipboard.writeText(this.generatedOtp).then(() => {
                this.$bvToast.toast(this.$t('interveners.otp.copied'), {
                    title: this.$t('interveners.otp.copied_title'),
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 2000,
                });
            });
        },

        async generateOtp() {
            this.isOtpLoading = true;
            try {
                const otpBody = this.getOtpBody();

                if (!otpBody) {
                    this.$bvToast.toast(this.$t('interveners.otp.no_channel'), {
                        title: this.$t('interveners.otp.no_channel_title'),
                        variant: 'warning',
                        solid: true
                    });
                    return;
                }

                const response = await this.$intervenersService.createOTP({}, otpBody, this.intervener.uuid);
                const otpData = this.getOtpData(response);

                if (!otpData.otp_code) {
                    this.$bvToast.toast(this.$t('interveners.otp.generate_error'), {
                        title: 'Error',
                        variant: 'danger',
                        solid: true
                    });
                    return;
                }

                this.generatedOtp = otpData.otp_code;
                this.otpDeliveryChannel = otpBody.mobile_phone ? 'SMS' : 'email';
                this.otpRemainingSeconds = otpData.remainingSeconds;
                this.startOtpTimer();
                this.$bvModal.show(this.otpModalId);
            } catch (error) {
                this.$bvToast.toast(this.$t('interveners.otp.generate_error_request'), {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
                console.error(error);
            } finally {
                this.isOtpLoading = false;
            }
        },
        
        getOtpBody() {
            const contacts = this.intervener.contacts || {};
            const baseBody = {
                force_new: true,
                is_middle_office: true,
                time_offset_in_minutes: new Date().getTimezoneOffset()
            };
            
            const mobilePhone = contacts.mobile_phone && String(contacts.mobile_phone).trim();
            const mobilePrefix = contacts.key_mobile_phone_prefix && String(contacts.key_mobile_phone_prefix).trim();
            const email = contacts.email && String(contacts.email).trim();
            const preferredChannel = this.intervener.two_factor_channel || 'SMS';

            if (preferredChannel === 'SMS' && mobilePhone && mobilePrefix) {
                return {
                    ...baseBody,
                    mobile_phone: mobilePhone,
                    mobile_phone_prefix: mobilePrefix
                };
            }

            if (email) {
                return {
                    ...baseBody,
                    email
                };
            }

            return null;
        },

        getOtpData(response) {
            const responseData = response && response.data ? response.data : response;
            const payload = responseData && responseData.data ? responseData.data : responseData || {};
            const otpCode = payload.otp_code;

            return {
                otp_code: otpCode,
                remainingSeconds: Number(payload.timeout) || 0
            };
        },

        startOtpTimer() {
            this.clearOtpTimer();
            if (this.otpRemainingSeconds) {
                this.otpTimer = setInterval(this.updateOtpRemainingSeconds, 1000);
            }
        },

        updateOtpRemainingSeconds() {
            this.otpRemainingSeconds = Math.max(0, this.otpRemainingSeconds - 1);

            if (!this.otpRemainingSeconds) {
                this.clearOtpTimer();
            }
        },

        clearOtpTimer() {
            if (this.otpTimer) {
                clearInterval(this.otpTimer);
                this.otpTimer = null;
            }
        }
    },

    beforeDestroy() {
        this.clearOtpTimer();
    },

    computed: {
        otpModalId() {
            return `intervener-otp-modal-${this.intervener.uuid}`;
        },

        otpExpired() {
            return !this.otpRemainingSeconds;
        },

        otpDeliveryLabel() {
            if (this.otpDeliveryChannel === 'SMS') return 'SMS';
            if (this.otpDeliveryChannel === 'email') return 'email';
            return '';
        },

        intervenerFullName() {
            const personalData = this.intervener.personal_data || {};

            return [personalData.name, personalData.lastname_1, personalData.lastname_2]
                .filter(Boolean)
                .join(' ');
        },

        formattedOtpRemaining() {
            const minutes = Math.floor(this.otpRemainingSeconds / 60);
            const seconds = this.otpRemainingSeconds % 60;

            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
    }
};
